import { ENDPOINTS } from '@/configs/endpoints';
import { NoteEntity, noteSchema } from '@/entities/Notes';
import call from '@/utils/api/api';
import { schemeParse, schemeParseMultiple } from '@/utils/schemeParse';

import type { PdfDocumentNote, PdfNoteHighlightColor, PdfNotesChange } from './types';
import { resolveCreateAnchorType, toBackendAnchorPath, toPdfDocumentNote, toPdfDocumentNotes } from './utils';

export class PdfNotesApi {
  private readonly _listeners = new Set<(change: PdfNotesChange) => void>();

  private _emit(change: PdfNotesChange) {
    this._listeners.forEach((listener) => {
      listener(change);
    });
  }

  private async _listNotes(organizationId: string, documentId?: string): Promise<NoteEntity[]> {
    const params = new URLSearchParams();

    if (documentId) {
      params.append('artifact_type', 'document');
      params.append('artifact_id', documentId);
    }

    const endpoint = ENDPOINTS.note.list(organizationId);
    const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;

    const response = await call<{ items: NoteEntity[] }>({
      endpoint: url,
    });

    if (response.isError) {
      return [];
    }

    return schemeParseMultiple(noteSchema, response.data.items);
  }

  subscribe(listener: (change: PdfNotesChange) => void) {
    this._listeners.add(listener);

    return () => {
      this._listeners.delete(listener);
    };
  }

  async listAll(organizationId: string): Promise<PdfDocumentNote[]> {
    const notes = await this._listNotes(organizationId);

    return toPdfDocumentNotes(notes, organizationId).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }

  async listByDocument(organizationId: string, documentId: string): Promise<PdfDocumentNote[]> {
    const notes = await this._listNotes(organizationId, documentId);

    return toPdfDocumentNotes(notes, organizationId);
  }

  async getCountsByDocument(organizationId: string): Promise<Record<string, number>> {
    const notes = await this.listAll(organizationId);

    return notes.reduce<Record<string, number>>((acc, item) => {
      acc[item.documentId] = (acc[item.documentId] ?? 0) + 1;

      return acc;
    }, {});
  }

  async create(
    organizationId: string,
    payload: {
      documentId: string;
      documentName?: string;
      caption: string;
      text: string;
      pageNumber: number;
      quote?: string;
      rects?: PdfDocumentNote['rects'];
      anchorType?: PdfDocumentNote['anchorType'];
      point?: PdfDocumentNote['point'];
      path?: PdfDocumentNote['path'];
      color?: PdfDocumentNote['color'];
    },
  ): Promise<PdfDocumentNote | null> {
    const pageNumber = Math.max(1, Math.floor(payload.pageNumber));
    const rects = payload.rects ?? [];
    const anchorType = resolveCreateAnchorType({
      anchorType: payload.anchorType,
      point: payload.point,
      path: payload.path,
      rects,
    });

    const response = await call<NoteEntity>({
      endpoint: ENDPOINTS.note.create(organizationId),
      method: 'POST',
      data: {
        caption: payload.caption.trim(),
        text: payload.text.trim(),
        artifact_type: 'document',
        artifact_id: payload.documentId,
        anchor: {
          type: anchorType,
          document_page: pageNumber,
          quote: payload.quote?.trim() || null,
          rects: rects.length ? rects : null,
          point: payload.point ?? null,
          path: toBackendAnchorPath(payload.path),
          color: payload.color ?? null,
        },
      },
    });

    if (response.isError) {
      return null;
    }

    const parsed = schemeParse(noteSchema, response.data);

    if (!parsed.success) {
      return null;
    }

    const mapped = toPdfDocumentNote(parsed.data);

    if (!mapped) {
      return null;
    }

    const note = {
      ...mapped,
      organizationId,
    };

    this._emit({ organizationId, documentId: note.documentId });

    return note;
  }

  async update(
    organizationId: string,
    noteId: string,
    payload: {
      caption?: string;
      text?: string;
      color?: PdfNoteHighlightColor;
    },
  ): Promise<PdfDocumentNote | null> {
    const response = await call<NoteEntity>({
      endpoint: ENDPOINTS.note.update(organizationId, noteId),
      method: 'PUT',
      data: payload,
    });

    if (response.isError) {
      return null;
    }

    const parsed = schemeParse(noteSchema, response.data);

    if (!parsed.success) {
      return null;
    }

    const mapped = toPdfDocumentNote(parsed.data);

    if (!mapped) {
      return null;
    }

    const note = {
      ...mapped,
      organizationId,
    };

    this._emit({ organizationId, documentId: note.documentId });

    return note;
  }

  async delete(organizationId: string, noteId: string): Promise<void> {
    const response = await call({
      endpoint: ENDPOINTS.note.delete(organizationId, noteId),
      method: 'DELETE',
    });

    if (response.isError) {
      return;
    }

    this._emit({ organizationId, documentId: null });
  }
}
